import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiFetch from "./utils/api";
import Typeahead from "./components/Typeahead";
import "./ReportsPage.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import ResourceChart from "./ResourceChart";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
);

ModuleRegistry.registerModules([AllCommunityModule]);

const activityCols = [
  {
    key: "ADDITIONAL LEAVE",
    header: "Additional Leave (hrs)",
    field: "additionalLeave",
  },
  { key: "ANNUAL LEAVE", header: "Annual Leave (hrs)", field: "annualLeave" },
  {
    key: "MATERNITY LEAVE",
    header: "Maternity Leave (hrs)",
    field: "maternityLeave",
  },
  {
    key: "PATERNITY LEAVE",
    header: "Paternity Leave (hrs)",
    field: "paternityLeave",
  },
  {
    key: "PERSONAL LEAVE",
    header: "Personal Leave (hrs)",
    field: "personalLeave",
  },
  {
    key: "PUBLIC HOLIDAY",
    header: "Public Holiday (hrs)",
    field: "publicHoliday",
  },
  { key: "SICK", header: "Sick (hrs)", field: "sick" },
  {
    key: "TIME OFF IN LIEU",
    header: "Time Off In Lieu (hrs)",
    field: "timeOffInLieu",
  },
  { key: "TIMESHEET", header: "Timesheet (hrs)", field: "timesheet" },
  { key: "TRAVEL TIME", header: "Travel Time (hrs)", field: "travelTime" },
  {
    key: "UNPAID LEAVE",
    header: "Unpaid Leave (hrs)",
    field: "unpaidLeave",
  },
];

const ResourceOverview = ({ onClose } = {}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [resources, setResources] = useState([]);

  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null); // 1-12 or null
  const HOURS_PER_DAY = 7.5;
  const [subcodeBreakdown, setSubcodeBreakdown] = useState(null);
  const [projectSubcodeMatrix, setProjectSubcodeMatrix] = useState(null);
  const [activitySummaries, setActivitySummaries] = useState(0);
  const [activityMax, setActivityMax] = useState({});
  const gridRef = useRef(null);

  const [displayDays, setDisplayDays] = useState(true);
  const toggleDisplayDays = () => {
    setDisplayDays(!displayDays);
  };

  const fetchReport = async (y) => {
    setLoading(true);
    setError("");
    setSelectedResource(null);

    try {
      const body = await apiFetch(`/api/reports?year=${y}`);
      setRows(body.data || []);
    } catch (e) {
      setError(e.message || "Failed to load resource overview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(year);
  }, [year]);

  // Compute per-resource totals by querying the timesheets table and summing hours
  // where ActivityCode matches the explicit activity list provided by the user.
  useEffect(() => {
    if (!rows || rows.length === 0) {
      setActivitySummaries({});
      return;
    }
    (async () => {
      try {
        // de-duplicate resource codes
        // const resourcesInRows = Array.from(
        //   new Set(rows.map((r) => r.resource)),
        // );

        // build start/end based on year and optionally selectedMonth
        // const getRange = (y, m) => {
        //   if (m) {
        //     const mm = String(m).padStart(2, "0");
        //     const start = `${y}-${mm}-01`;
        //     const lastDay = new Date(y, m, 0).getDate();
        //     const end = `${y}-${mm}-${String(lastDay).padStart(2, "0")}`;
        //     return { start, end };
        //   }
        //   return { start: `${y}-01-01`, end: `${y}-12-31` };
        // };

        // const range = getRange(year, selectedMonth);
        const summaries = await apiFetch("/api/reports/user-pto", {
          method: "POST",
          body: { users: rows.map((user) => user.id), year: String(year) },
        });
        // await Promise.all(
        //   resourcesInRows.map(async (resCode) => {
        //     try {
        //       // Prefer querying by numeric user id if available, otherwise fall back to resource code
        //       const userObj =
        //         resources && resources.length
        //           ? resources.find((u) => u.code === resCode)
        //           : null;
        //       const userParam = userObj
        //         ? encodeURIComponent(userObj.id)
        //         : encodeURIComponent(resCode);
        //       const qs = `user=${userParam}&start=${range.start}&end=${range.end}`;
        //       const resp = await apiFetch(`/api/timesheets?${qs}`);
        //       const entries = Array.isArray(resp) ? resp : [];
        //       const byTarget = {
        //         additionalLeave: 0,
        //         annualLeave: 0,
        //         maternityLeave: 0,
        //         paternityLeave: 0,
        //         personalLeave: 0,
        //         sick: 0,
        //         timeOffInLieu: 0,
        //         timesheet: 0,
        //         travelTime: 0,
        //         unpaidLeave: 0,
        //       };
        //       for (const e of entries) {
        //         const act = (
        //           e.ActivityCode ||
        //           e.Activity ||
        //           e.TimesheetActivity ||
        //           ""
        //         ).toLowerCase();
        //         const hours =
        //           Number(e.TimesheetHours || e.Hours || e.hours || 0) || 0;
        //         if (!act) continue;
        //         if (act.indexOf("additional leave") !== -1)
        //           byTarget.additionalLeave += hours;
        //         else if (act.indexOf("annual leave") !== -1)
        //           byTarget.annualLeave += hours;
        //         else if (act.indexOf("maternity leave") !== -1)
        //           byTarget.maternityLeave += hours;
        //         else if (act.indexOf("paternity leave") !== -1)
        //           byTarget.paternityLeave += hours;
        //         else if (act.indexOf("personal leave") !== -1)
        //           byTarget.personalLeave += hours;
        //         else if (act.indexOf("sick") !== -1) byTarget.sick += hours;
        //         else if (act.indexOf("time off in lieu") !== -1)
        //           byTarget.timeOffInLieu += hours;
        //         else if (act.indexOf("timesheet") !== -1)
        //           byTarget.timesheet += hours;
        //         else if (act.indexOf("travel time") !== -1)
        //           byTarget.travelTime += hours;
        //         else if (act.indexOf("unpaid leave") !== -1)
        //           byTarget.unpaidLeave += hours;
        //       }
        //       summaries[resCode] = byTarget;
        //     } catch (e) {
        //       summaries[resCode] = {
        //         additionalLeave: 0,
        //         annualLeave: 0,
        //         maternityLeave: 0,
        //         paternityLeave: 0,
        //         personalLeave: 0,
        //         sick: 0,
        //         timeOffInLieu: 0,
        //         timesheet: 0,
        //         travelTime: 0,
        //         unpaidLeave: 0,
        //       };
        //     }
        //   }),
        // );
        setActivitySummaries(summaries.breakdown);
        setActivityMax(summaries.max);
      } catch (e) {
        setActivitySummaries({});
        setActivityMax(0);
      }
    })();
  }, [rows, year, selectedMonth, resources]);

  useEffect(() => {
    (async () => {
      setSelectedLoading(true);
      if (!selectedResource) {
        setSubcodeBreakdown(null);
        setProjectSubcodeMatrix(null);
        return;
      }
      try {
        const r = encodeURIComponent(selectedResource.id);
        const monthParam = selectedMonth ? `&month=${selectedMonth}` : "";
        const res = await apiFetch(
          `/api/reports/resource-subcodes?year=${year}&resource=${r}${monthParam}`,
        );
        setSubcodeBreakdown(res && res.breakdown ? res.breakdown : null);
        const res2 = await apiFetch(
          `/api/reports/resource-project-subcodes?year=${year}&resource=${r}${monthParam}`,
        );
        setProjectSubcodeMatrix(res2 && res2.matrix ? res2 : null);
      } catch (e) {
        setSubcodeBreakdown(null);
        setProjectSubcodeMatrix(null);
      }
      setSelectedLoading(false);
    })();
  }, [selectedResource, year, selectedMonth]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/users");
        const list = Array.isArray(res)
          ? res.users || res
          : res && Array.isArray(res.users)
            ? res.users
            : [];
        // normalize to { code, desc } so Typeahead filters by code/desc like projects
        setResources(
          list.map((u) => ({
            id: u.UserID,
            code: u.UserCode,
            desc: u.UserDescription,
          })),
        );
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const getHeatmapStyle = useCallback(
    (val) => {
      const max = Number(activityMax || 0);
      const pct =
        max > 0 ? ((Number(val || 0) / max) * 100).toFixed(2) : "0.00";
      return {
        backgroundColor: `color-mix(in oklab, #f4f4f4, #56a6be ${pct}%)`,
        textAlign: "right",
      };
    },
    [activityMax],
  );

  const tableRows = useMemo(() => {
    return rows
      .filter(
        (r) =>
          !selectedResource ||
          r.resource === (selectedResource.code || selectedResource),
      )
      .map((r) => {
        const sums = activitySummaries[r.resource] || {
          "ADDITIONAL LEAVE": 0,
          "ANNUAL LEAVE": 0,
          "MATERNITY LEAVE": 0,
          "PATERNITY LEAVE": 0,
          "PERSONAL LEAVE": 0,
          "PUBLIC HOLIDAY": 0,
          SICK: 0,
          "TIME OFF IN LIEU": 0,
          TIMESHEET: 0,
          "TRAVEL TIME": 0,
          "UNPAID LEAVE": 0,
        };

        const totalHours = activityCols.reduce(
          (acc, c) => acc + Number(sums[c.key] || 0),
          0,
        );

        return {
          resource: r.resource,
          additionalLeave: Number(sums["ADDITIONAL LEAVE"] || 0),
          annualLeave: Number(sums["ANNUAL LEAVE"] || 0),
          maternityLeave: Number(sums["MATERNITY LEAVE"] || 0),
          paternityLeave: Number(sums["PATERNITY LEAVE"] || 0),
          personalLeave: Number(sums["PERSONAL LEAVE"] || 0),
          publicHoliday: Number(sums["PUBLIC HOLIDAY"] || 0),
          sick: Number(sums["SICK"] || 0),
          timeOffInLieu: Number(sums["TIME OFF IN LIEU"] || 0),
          timesheet: Number(sums["TIMESHEET"] || 0),
          travelTime: Number(sums["TRAVEL TIME"] || 0),
          unpaidLeave: Number(sums["UNPAID LEAVE"] || 0),
          totalHours,
          totalDays: totalHours / HOURS_PER_DAY,
        };
      });
  }, [rows, selectedResource, activitySummaries]);

  const columnDefs = useMemo(() => {
    return [
      {
        field: "resource",
        headerName: "Resource",
        minWidth: 140,
        flex: 1.4,
        pinned: "left",
      },
      ...activityCols.map((c) => ({
        field: c.field,
        headerName: c.header,
        minWidth: 110,
        flex: 1,
        type: "numericColumn",
        cellStyle: (params) => getHeatmapStyle(params.value),
        valueFormatter: (params) => Number(params.value || 0).toFixed(2),
      })),
      {
        field: displayDays ? "totalDays" : "totalHours",
        headerName: displayDays ? "Total (days)" : "Total (Hours)",
        minWidth: 120,
        flex: 1,
        type: "numericColumn",
        valueFormatter: (params) => Number(params.value || 0).toFixed(2),
        cellStyle: { textAlign: "right", fontWeight: 700 },
      },
    ];
  }, [displayDays, getHeatmapStyle]);

  function exportToCSV() {
    const api = gridRef.current?.api;
    if (!api) return;

    const resourceCode = selectedResource?.code
      ? `-${selectedResource.code}`
      : "";
    const monthSuffix = selectedMonth ? `-month-${selectedMonth}` : "";
    api.exportDataAsCsv({
      fileName: `resource-pto-${year}${monthSuffix}${resourceCode}.csv`,
    });
  }

  function exportToPDF() {
    try {
      const baseTitle = `Resource PTO ${selectedResource ? selectedResource.code : ""} ${year}${selectedMonth ? ` Month ${selectedMonth}` : ""}`;
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const title = `${baseTitle} ${dateStr}`;

      const headerHtml = activityCols
        .map(
          (col) =>
            `<th style="border:1px solid #ddd;padding:6px;text-align:left;">${col.header}</th>`,
        )
        .join("");

      const rowsHtml = (tableRows || [])
        .map((row) => {
          const activityCells = activityCols
            .map((col) => {
              const value = Number(row[col.field] || 0);
              const max = Number(activityMax || 0);
              const pct = max > 0 ? ((value / max) * 100).toFixed(2) : "0.00";
              const bg = `color-mix(in oklab, #f4f4f4, #56a6be ${pct}%)`;
              return `<td style="border:1px solid #eee;padding:6px;text-align:right;background:${bg};">${value.toFixed(2)}</td>`;
            })
            .join("");

          const totalValue = displayDays
            ? Number(row.totalDays || 0)
            : Number(row.totalHours || 0);
          return `<tr>
            <td style="border:1px solid #eee;padding:6px;">${row.resource || ""}</td>
            ${activityCells}
            <td style="border:1px solid #eee;padding:6px;text-align:right;font-weight:700;">${totalValue.toFixed(2)}</td>
          </tr>`;
        })
        .join("");

      const doc = window.open("", "_blank");
      if (!doc) throw new Error("Unable to open print window (popup blocked)");
      doc.document.write(`<!doctype html><html><head><title>${title}</title>`);
      try {
        doc.document.title = title;
      } catch (e) {}
      doc.document.write(
        `<style>body{font-family:Arial,Helvetica,sans-serif;padding:20px} table{border-collapse:collapse;width:100%} th,td{border:1px solid #ddd;padding:6px}</style>`,
      );
      doc.document.write(`</head><body>`);
      doc.document.write(`<h2>${baseTitle}</h2>`);
      doc.document.write(
        `<table><thead><tr><th style="border:1px solid #ddd;padding:6px;text-align:left;">Resource</th>${headerHtml}<th style="border:1px solid #ddd;padding:6px;text-align:left;">${displayDays ? "Total (days)" : "Total (Hours)"}</th></tr></thead><tbody>${rowsHtml}</tbody></table>`,
      );
      doc.document.write(`</body></html>`);
      doc.document.close();
      setTimeout(() => {
        try {
          doc.print();
        } catch (e) {
          console.warn("Print failed:", e);
        }
        doc.close();
      }, 600);
    } catch (e) {
      console.error(e);
      alert("Export failed: " + (e && e.message ? e.message : String(e)));
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>Resource PTO</h2>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label style={{ fontWeight: 600 }}>Year</label>
        <button onClick={() => setYear((y) => y - 1)}>-</button>
        <input
          type="number"
          value={year}
          onChange={(e) =>
            setYear(Number(e.target.value || new Date().getFullYear()))
          }
          style={{ width: 100 }}
        />
        <button onClick={() => setYear((y) => y + 1)}>+</button>
        <div style={{ marginLeft: 12 }}>
          <button onClick={() => fetchReport(year)}>Refresh</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={exportToPDF}>Export to PDF</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={exportToCSV}>Export CSV</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={toggleDisplayDays}>
            {displayDays ? "Show Totals in Hours" : "Show Totals in Days"}
          </button>
        </div>
        <div style={{ marginLeft: 12, minWidth: 260 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>Resource (search)</div>
          <Typeahead
            items={resources.filter((v) =>
              rows.some((r) => r.resource === v.code),
            )}
            value={selectedResource}
            onChange={(it) => setSelectedResource(it)}
            placeholder="Type resource/user"
            getItemKey={(it) => it.code}
            getItemLabel={(it) => `${it.code} — ${it.desc}`}
            renderItem={(it) => (
              <div>
                <strong style={{ marginRight: 8 }}>{it.code}</strong>
                <span>{it.desc}</span>
              </div>
            )}
          />
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <div style={{ color: "darkred" }}>{error}</div>
      ) : (
        <div style={{ overflowX: "visible" }}>
          <div style={{ width: "100%", overflowX: "auto" }}>
            <div
              className="ag-theme-balham resource-pto-grid"
              style={{ width: "100%" }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={tableRows}
                columnDefs={columnDefs}
                domLayout="autoHeight"
                rowStyle={{ backgroundColor: "transparent" }}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  minWidth: 90,
                  cellStyle: { backgroundColor: "transparent" },
                }}
                onRowClicked={(event) => {
                  setSelectedResource(
                    resources.find(
                      (element) => element.code === event.data.resource,
                    ) || null,
                  );
                }}
              />
            </div>
          </div>

          {/* Month selector: sits below the table but above the charts. */}
          {selectedResource && (
            <div style={{ marginTop: 12, marginBottom: 12 }}>
              <label style={{ fontWeight: 600, marginRight: 8 }}>Month</label>
              <select
                value={selectedMonth || ""}
                onChange={(e) =>
                  setSelectedMonth(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">All</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <label style={{ marginLeft: 8, color: "darkred" }}>
                {selectedLoading ? "Loading..." : ""}
              </label>
            </div>
          )}

          {selectedResource &&
            subcodeBreakdown &&
            subcodeBreakdown.length > 0 && (
              <ResourceChart
                selectedResource={selectedResource}
                subcodeBreakdown={subcodeBreakdown}
                projectSubcodeMatrix={projectSubcodeMatrix}
                year={year}
                month={selectedMonth}
                onClose={onClose}
                loading={setSelectedLoading}
              />
            )}
          {selectedResource &&
            subcodeBreakdown &&
            subcodeBreakdown.length === 0 && (
              <>
                <p>Selected resource has no activity for this period.</p>
              </>
            )}
        </div>
      )}
    </div>
  );
};

export default ResourceOverview;
